import React from 'react';
import './i18n';
import { GoalsProvider } from './hooks/useGoals';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from 'styled-components';
import { theme, themeLight, GlobalStyle } from './styles/theme';
import Layout from './components/Layout/Layout';
import SettingsCallback from './pages/SettingsCallback';
import Dashboard from './pages/Dashboard';
import LandingPage from './landing/LandingPage';
import Leads from './pages/Leads';
import NewLeadPage from './pages/NewLeadPage';
import Sales from './pages/Sales';
import NewSalePage from './pages/NewSalePage';
import { useCurrentUser } from './hooks/useCurrentUser';
import { SubscriptionStatusProvider, useSubscriptionStatusContext } from './hooks/SubscriptionStatusContext';
import { ProfilePhotoProvider } from './hooks/useProfilePhoto';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import NewScriptPage from './pages/NewScriptPage';
import Profile from './pages/Profile';
import Reports from './pages/Reports';
import Subscription from './pages/Subscription';
import Upgrade from './pages/Upgrade';
import NeuralCore from './pages/BotsAndAutomations/NeuralCore';
import BotConfigs from './pages/BotsAndAutomations/BotConfigs';
import Integrations from './pages/BotsAndAutomations/Integrations';
import Media from './pages/BotsAndAutomations/Media';
import Monitoring from './pages/BotsAndAutomations/Monitoring';
import WhatsAppIntegration from './pages/WhatsAppIntegration';
import WhatsAppCallback from './pages/WhatsAppCallback';
import GmailIntegration from './pages/GmailIntegration';
import GmailConfig from './pages/GmailConfig';
import GmailCallback from './pages/GmailCallback';
import AutomationFlows from './pages/AutomationFlows';
import Automations from './pages/Automations';
import GmailAutomation from './pages/Automations/GmailAutomation';
import AutomationsList from './pages/AutomationsList';
import GmailAutomationSimple from './components/Gmail/GmailAutomationSimple';
import Braip from './pages/Braip';
import Philosophy from './pages/Philosophy';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

// Loading Global Style (sem theme dependency)
const LoadingGlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  html {
    font-size: 16px;
    scroll-behavior: smooth;
    background: #0a0a0a !important;
  }
  body {
    font-family: 'Inter', sans-serif;
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5;
    color: #fafafa;
    background-color: #0a0a0a !important;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  #root {
    background: #0a0a0a !important;
    min-height: 100vh;
  }
`;


const fadeIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.95);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const fadeOut = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

// Loading components ultra minimalista
const AppLoadingScreen = styled.div<{ $isExiting: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: ${props => props.theme?.colors?.background ?? '#0a0a0a'} !important;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 999999999;
  animation: ${props => props.$isExiting ? fadeOut : fadeIn} 0.5s ease-out forwards;

  /* Força prioridade absoluta - evita qualquer flash */
  overflow: hidden;
  pointer-events: auto;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => props.theme?.colors?.background ?? '#0a0a0a'} !important;
    z-index: -1;
  }
`;

const AppLoadingLogo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme?.spacing?.[2] ?? '0.5rem'};
  margin-bottom: ${props => props.theme?.spacing?.[6] ?? '1.5rem'};
`;

const AppLoadingRing = styled.div`
  position: relative;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme?.colors?.backgroundSecondary ?? '#232526'};
  box-shadow: ${props => props.theme?.shadows?.md ?? '0 4px 6px -1px rgba(0, 0, 0, 0.18), 0 2px 4px -1px rgba(0, 0, 0, 0.10)'};

  &::before {
    content: '';
    position: absolute;
    inset: -3px;
    border-radius: inherit;
    border: 2px solid transparent;
    border-top-color: ${props => props.theme?.colors?.primary ?? '#ff3b30'};
    border-right-color: ${props => props.theme?.colors?.primary ?? '#ff3b30'};
    border-bottom-color: ${props => props.theme?.colors?.borderLight ?? '#404040'};
    border-left-color: ${props => props.theme?.colors?.borderLight ?? '#404040'};
    animation: ${spin} 1s linear infinite;
  }
`;

const AppLoadingIcon = styled.img`
  width: 36px;
  height: 36px;
  object-fit: contain;
  z-index: 1;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.4));
`;

// Wrapper com fade suave para conteúdo (usa prop transitória para não vazar ao DOM)
const ContentWrapper = styled.div<{ $show: boolean }>`
  opacity: ${props => props.$show ? 1 : 0};
  transform: ${props => props.$show ? 'translateY(0)' : 'translateY(20px)'};
  transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  min-height: 100vh;
  background: #0a0a0a;
`;

function App() {
  // Sempre chamar hooks de terceiros primeiro
  const { user } = useCurrentUser();

  const [themeMode, setThemeMode] = React.useState<'dark' | 'light'>(() => {
    const savedTheme = localStorage.getItem('loopra-theme');
    console.log('[THEME] Tema salvo no localStorage:', savedTheme);
    // Se não há tema salvo, define dark mode como padrão
    if (!savedTheme) {
      localStorage.setItem('loopra-theme', 'dark');
      console.log('[THEME] Definindo dark mode como padrão');
      return 'dark';
    }
    return savedTheme as 'dark' | 'light';
  });
  const [loadingStage, setLoadingStage] = React.useState<'loading' | 'complete' | 'exiting'>('loading');
  const [showContent, setShowContent] = React.useState(false);
  const [isInitialMount, setIsInitialMount] = React.useState(true);

  const handleToggleTheme = React.useCallback(() => {
    setThemeMode((prev) => {
      const newTheme = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem('loopra-theme', newTheme);
      return newTheme;
    });
  }, []);

  // Remove o overlay HTML imediatamente quando React carrega
  React.useEffect(() => {
    const overlay = document.getElementById('initial-overlay');
    if (overlay) {
      overlay.remove();
    }
  }, []);

  // Sistema ultra minimalista com prioridade absoluta para loading
  React.useEffect(() => {
    let exitTimeout: ReturnType<typeof setTimeout> | undefined;

    const loadingSequence = async () => {
      // Garante que o loading sempre apareça primeiro
      setIsInitialMount(false);

      // Pequeno delay para garantir que o loading renderizou
      await new Promise(resolve => setTimeout(resolve, 100));

      // Tempo de exibição controlado
      await new Promise(resolve => setTimeout(resolve, 700));

      // Fade out
      setLoadingStage('exiting');

      // Mostra conteúdo
      exitTimeout = setTimeout(() => {
        setLoadingStage('complete');
        setShowContent(true);
      }, 420);
    };

    // Inicia loading imediatamente, independente do userLoading
    loadingSequence();

    return () => {
      if (exitTimeout) {
        clearTimeout(exitTimeout);
      }
    };
  }, []);

  // Força loading screen absoluto - evita qualquer flash
  if (loadingStage !== 'complete' || isInitialMount) {
    return (
      <>
        <LoadingGlobalStyle />
        <AppLoadingScreen $isExiting={loadingStage === 'exiting'}>
          <AppLoadingLogo>
            <AppLoadingRing>
              <AppLoadingIcon
                src="/icons/loopra_icon_profile.webp"
                alt="Loopra"
                draggable={false}
              />
            </AppLoadingRing>
          </AppLoadingLogo>
        </AppLoadingScreen>
      </>
    );
  }


  return (
    <GoalsProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={themeMode === 'light' ? themeLight : theme}>
          <GlobalStyle />
          <ProfilePhotoProvider>
            <SubscriptionStatusProvider user={user}>
              <ContentWrapper $show={showContent}>
                <Router>
                  <AppRoutes themeMode={themeMode} onToggleTheme={handleToggleTheme} />
                </Router>
              </ContentWrapper>
            </SubscriptionStatusProvider>
          </ProfilePhotoProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </GoalsProvider>
  );
}

function AppRoutes({ themeMode, onToggleTheme }: { themeMode: 'dark' | 'light', onToggleTheme: () => void }) {
  const location = useLocation();
  const { status } = useSubscriptionStatusContext();

  // Verifica se tem assinatura válida
  const hasValidSubscription = !status.loading &&
                               !status.error &&
                               status.plano &&
                               status.plano.trim() !== '' &&
                               status.plano !== 'null';

  // Auto-redirect bidirecional
  React.useEffect(() => {
    if (hasValidSubscription && location.pathname === '/') {
      console.log('[DEBUG] Auto-redirect: Usuário tem assinatura, redirecionando para /home');
      // Usando replace para evitar voltar para landing page no histórico
      window.history.replaceState(null, '', '/home');
    } else if (!hasValidSubscription && !status.loading && location.pathname !== '/' && location.pathname !== '/settings-callback') {
      console.log('[DEBUG] Auto-redirect: Usuário sem assinatura, redirecionando para landing');
      // Quando perde assinatura (logout ou expiração), volta para landing
      window.history.replaceState(null, '', '/');
    }
  }, [hasValidSubscription, location.pathname, status.loading]);

  return (
    <Routes>
      {/* Landing page at root */}
      <Route path="/" element={<LandingPage />} />

      {/* Public Philosophy Page (with landing navbar/footer) */}
      <Route path="/philosophy" element={<Philosophy />} />

      {/* Protected/app routes: Layout e páginas só renderizam se assinatura válida */}
      <Route element={<ProtectedRouteWrapper><Layout /></ProtectedRouteWrapper>}>
        <Route path="home" element={<Dashboard themeMode={themeMode} onToggleTheme={onToggleTheme} />} />
        <Route path="leads" element={<Leads />} />
        <Route path="leads/new" element={<NewLeadPage />} />
        <Route path="leads/edit/:id" element={<NewLeadPage />} />
        <Route path="sales" element={<Sales />} />
        <Route path="sales/new" element={<NewSalePage />} />
        <Route path="braip" element={<Braip />} />
        <Route path="profile" element={<Profile />} />
        <Route path="subscription" element={<Subscription />} />
        <Route path="upgrade" element={<Upgrade />} />
        <Route path="reports" element={<Reports />} />

        {/* Bots and Automations */}
        <Route path="bots-automations/neural-core" element={<NeuralCore />} />
        <Route path="bots-automations/bot-settings" element={<BotConfigs />} />
        <Route path="bots-automations/media" element={<Media />} />
        <Route path="bots-automations/logs" element={<Monitoring />} />

        {/* This route seems to be related to neural-vault, so I'll keep it under the new path */}
        <Route path="bots-automations/neural-core/scripts/new" element={<NewScriptPage />} />

        {/* Gmail Integration */}
        <Route path="gmail" element={<GmailIntegration />} />
        <Route path="gmail/config" element={<GmailConfig />} />

        {/* Gmail Automation Routes */}
        <Route path="gmail/automations" element={<AutomationsList />} />
        <Route path="gmail/automations/new" element={<GmailAutomationSimple />} />
        <Route path="gmail/automations/:id/edit" element={<GmailAutomationSimple />} />
        
        {/* Legacy Routes (pode remover depois) */}
        <Route path="gmail/automation" element={<AutomationsList />} />
        <Route path="gmail/automation/create" element={<AutomationFlows />} />
        <Route path="gmail/automation/edit/:id" element={<AutomationFlows />} />

        {/* Automations Hub (New Modular Architecture) */}
        <Route path="automations" element={<Automations />} />
        <Route path="automations/gmail" element={<GmailAutomation />} />

        {/* WhatsApp Integration */}
        <Route path="bots-automations/integra" element={<WhatsAppIntegration />} />
      </Route>

      <Route path="/settings-callback" element={<SettingsCallback />} />
      <Route path="/whatsapp/callback" element={<WhatsAppCallback />} />
      <Route path="/gmail/callback" element={<GmailCallback />} />

      {/* Catch-all: qualquer rota inexistente volta para landing */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function ProtectedRouteWrapper({ children }: { children: React.ReactElement }) {
  const location = useLocation();
  const { status } = useSubscriptionStatusContext();

  // Se ainda está carregando a assinatura, aguarda
  if (status.loading) {
    return <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#050505', color: '#fff'}}>Verificando assinatura...</div>;
  }

  // Verifica se tem assinatura válida
  const hasValidSubscription = status.plano &&
                               status.plano.trim() !== '' &&
                               status.plano !== 'null';

  console.log('[DEBUG] ProtectedRouteWrapper:', {
    plano: status.plano,
    hasValidSubscription,
    location: location.pathname,
    action: hasValidSubscription ? '✅ LIBERANDO' : '❌ BLOQUEANDO'
  });

  if (hasValidSubscription) {
    return children;
  }

  // Se está tentando acessar rota protegida sem assinatura, bloqueia acesso
  // Evita loop verificando se já está na landing page
  if (location.pathname !== '/') {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Se já está na landing page, não redireciona
  return children;
}

export default App;
